import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getApiBaseUrl } from '../../config/apiBase';
import { 
  Save, 
  X, 
  RefreshCw, 
  Play, 
  Square, 
  Settings,
  FileText,
  Activity,
  CheckCircle,
  Clock,
  Server,
  Eye,
  Filter,
  AlertTriangle,
  RotateCcw,
  PlayCircle,
  XCircle,
  MemoryStick,
  Hash,
  Link2
} from 'lucide-react';
import SearchInput from '../../components/common/SearchInput';

const READ_ONLY_WORKERS = {
  dlr_processor: { parent: 'smpp_worker', label: 'SMPP Submit Worker' },
};

// Soft workers can be paused/resumed via DB toggle (no systemd restart needed)
const SOFT_WORKERS = {
  pending_retry: { parent: 'smpp_worker', label: 'Runs inside SMPP Submit Worker' },
};

const DEPRECATED_WORKERS = {};

const WorkerManagement = () => {
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLogsDrawer, setShowLogsDrawer] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configData, setConfigData] = useState({});
  const [configSaving, setConfigSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchWorkers();
  }, []);

  useEffect(() => {
    let filtered = workers;
    if (searchTerm) {
      filtered = filtered.filter(w =>
        w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(w => w.status === statusFilter);
    }
    setFilteredWorkers(filtered);
  }, [workers, searchTerm, statusFilter]);

  const fetchWorkers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkers(Array.isArray(data) ? data : []);
      } else if (response.status === 403) {
        toast.error('Access denied. Master Admin role required.');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWorkers();
  };

  const handleWorkerAction = async (workerCode, action) => {
    const key = `${workerCode}-${action}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${workerCode}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || `Worker ${action} successful`);
        setTimeout(fetchWorkers, 1500);
      } else {
        toast.error(data.error || `Failed to ${action} worker`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} worker`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleViewConfig = async (worker) => {
    setSelectedWorker(worker);
    setConfigData({});
    setShowConfigModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${worker.code}/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(item => { map[item.config_key] = item.config_value; });
          setConfigData(map);
        } else {
          setConfigData(data.config || data || {});
        }
      }
    } catch (error) {
      toast.error('Failed to fetch worker configuration');
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedWorker) return;
    setConfigSaving(true);
    try {
      const token = localStorage.getItem('token');
      let payload;
      if (selectedWorker.code === 'pending_retry') {
        // pending_retry config: send typed values directly (booleans stay booleans, ints stay ints)
        payload = {
          enabled: !!configData.enabled,
          interval_sec: parseInt(configData.interval_sec) || 10,
          window_hours: parseInt(configData.window_hours) || 48,
          batch_size: parseInt(configData.batch_size) || 100,
          max_retries: parseInt(configData.max_retries != null ? configData.max_retries : 0),
        };
      } else {
        // Generic: convert string values to numbers for int fields
        payload = {};
        Object.entries(configData).forEach(([key, value]) => {
          const num = Number(value);
          payload[key] = isNaN(num) ? value : num;
        });
      }
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${selectedWorker.code}/config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success('Configuration saved successfully');
        setShowConfigModal(false);
        fetchWorkers();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds || seconds <= 0) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const getStatusBadge = (status) => {
    const map = {
      running: { color: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle className="w-3.5 h-3.5" /> },
      stopped: { color: 'bg-red-100 text-red-700 border-red-200', icon: <XCircle className="w-3.5 h-3.5" /> },
      restarting: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: <RefreshCw className="w-3.5 h-3.5 animate-spin" /> },
      error:   { color: 'bg-red-100 text-red-700 border-red-200', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    };
    const s = map[status] || { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <Clock className="w-3.5 h-3.5" /> };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border ${s.color}`}>
        {s.icon} {status?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Server className="w-7 h-7 text-indigo-600" />
              Worker Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">Monitor and manage systemd background workers</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search workers by name or code..."
            className="flex-1"
          />
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="running">Running</option>
              <option value="stopped">Stopped</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Total Workers</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{workers.length}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg"><Server className="w-6 h-6 text-indigo-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Running</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{workers.filter(w => w.status === 'running').length}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg"><PlayCircle className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Stopped</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{workers.filter(w => w.status === 'stopped').length}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg"><Square className="w-6 h-6 text-red-600" /></div>
          </div>
        </div>
      </div>

      {/* Workers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredWorkers.map((worker) => (
          <div key={worker.code} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:border-indigo-200 transition-all duration-300 overflow-hidden">
            <div className="p-6">
              {/* Header row */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl ${worker.status === 'running' ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <Activity className={`w-6 h-6 ${worker.status === 'running' ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{worker.name}</h3>
                    <p className="text-sm text-gray-500 font-mono">{worker.code}</p>
                  </div>
                </div>
                {getStatusBadge(worker.status)}
              </div>

              {/* Description */}
              {worker.description && (
                <p className="text-sm text-gray-600 mb-4">{worker.description}</p>
              )}

              {/* Read-Only Worker Indicator */}
              {READ_ONLY_WORKERS[worker.code] && (
                <div className="flex items-center gap-1.5 mb-4 text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md px-2.5 py-1.5 w-fit">
                  <Link2 className="w-3.5 h-3.5" />
                  <span>Runs inside <strong>{READ_ONLY_WORKERS[worker.code].label}</strong> — control via parent service</span>
                </div>
              )}

              {/* Soft Worker Indicator */}
              {SOFT_WORKERS[worker.code] && (
                <div className={`flex items-center gap-1.5 mb-4 text-xs ${worker.status === 'stopped' ? 'text-red-600 bg-red-50 border-red-200' : 'text-amber-600 bg-amber-50 border-amber-200'} border rounded-md px-2.5 py-1.5 w-fit`}>
                  <Link2 className="w-3.5 h-3.5" />
                  <span>{SOFT_WORKERS[worker.code].label} — {worker.status === 'stopped' ? <strong>PAUSED (auto-retry disabled)</strong> : <strong>ACTIVE (auto-retry enabled)</strong>}</span>
                </div>
              )}

              {/* Deprecated Worker Indicator */}
              {DEPRECATED_WORKERS[worker.code] && (
                <div className="flex items-center gap-1.5 mb-4 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-2.5 py-1.5 w-fit">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span><strong>DEPRECATED</strong> — {DEPRECATED_WORKERS[worker.code].label}</span>
                </div>
              )}

              {/* Metrics grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                    <Hash className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">PID</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 font-mono">{worker.pid || '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Uptime</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{formatUptime(worker.uptime_seconds)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                    <MemoryStick className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Memory</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{worker.memory_usage && worker.memory_usage !== '0' ? worker.memory_usage : '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                    <Activity className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Heartbeat</span>
                  </div>
                  <p className={`text-sm font-semibold ${worker.heartbeat_status === 'ACTIVE' ? 'text-green-600' : 'text-gray-400'}`}>
                    {worker.heartbeat_status === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                {READ_ONLY_WORKERS[worker.code] ? (
                  <span className="text-xs text-gray-400 italic py-1.5">Controlled by {READ_ONLY_WORKERS[worker.code].label}</span>
                ) : SOFT_WORKERS[worker.code] ? (
                  worker.status === 'running' ? (
                    <button
                      onClick={() => handleWorkerAction(worker.code, 'stop')}
                      disabled={actionLoading[`${worker.code}-stop`]}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 text-xs font-bold transition-colors"
                    >
                      {actionLoading[`${worker.code}-stop`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                      Pause Auto-Retry
                    </button>
                  ) : (
                    <button
                      onClick={() => handleWorkerAction(worker.code, 'start')}
                      disabled={actionLoading[`${worker.code}-start`]}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50 text-xs font-bold transition-colors"
                    >
                      {actionLoading[`${worker.code}-start`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                      Resume Auto-Retry
                    </button>
                  )
                ) : worker.status === 'running' ? (
                  <>
                    <button
                      onClick={() => handleWorkerAction(worker.code, 'restart')}
                      disabled={actionLoading[`${worker.code}-restart`]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 text-xs font-medium transition-colors"
                    >
                      {actionLoading[`${worker.code}-restart`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      Restart
                    </button>
                    <button
                      onClick={() => handleWorkerAction(worker.code, 'stop')}
                      disabled={actionLoading[`${worker.code}-stop`]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 text-xs font-medium transition-colors"
                    >
                      {actionLoading[`${worker.code}-stop`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                      Stop
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleWorkerAction(worker.code, 'start')}
                    disabled={actionLoading[`${worker.code}-start`]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 text-xs font-medium transition-colors"
                  >
                    {actionLoading[`${worker.code}-start`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    Start
                  </button>
                )}
                <button
                  onClick={() => { setSelectedWorker(worker); setShowLogsDrawer(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 text-xs font-medium transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Logs
                </button>
                <button
                  onClick={() => handleViewConfig(worker)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Config
                </button>
                <button
                  onClick={() => { setSelectedWorker(worker); setShowDetailsModal(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Details
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredWorkers.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Server className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No workers found</p>
          </div>
        )}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && selectedWorker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Configuration — {selectedWorker.name}</h3>
              <button onClick={() => setShowConfigModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {Object.keys(configData).length > 0 ? (
                selectedWorker.code === 'pending_retry' ? (
                  /* Pending Retry Worker — custom form with proper controls */
                  <>
                    {/* Enabled Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-semibold text-gray-800">Enabled</label>
                        <p className="text-xs text-gray-500">Enable or disable the pending SMS retry loop</p>
                      </div>
                      <button
                        onClick={() => setConfigData({...configData, enabled: !configData.enabled})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          configData.enabled ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          configData.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Interval */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Retry Interval (seconds)
                        <span className="text-xs text-gray-400 ml-2">Range: 5–120</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range" min="5" max="120" step="5"
                          value={configData.interval_sec || 10}
                          onChange={(e) => setConfigData({...configData, interval_sec: parseInt(e.target.value)})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                        <span className="text-sm font-mono font-semibold text-indigo-600 w-12 text-right">{configData.interval_sec || 10}s</span>
                      </div>
                    </div>

                    {/* Window Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Retry Window (hours)
                        <span className="text-xs text-gray-400 ml-2">Range: 1–72</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range" min="1" max="72" step="1"
                          value={configData.window_hours || 48}
                          onChange={(e) => setConfigData({...configData, window_hours: parseInt(e.target.value)})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                        <span className="text-sm font-mono font-semibold text-indigo-600 w-12 text-right">{configData.window_hours || 48}h</span>
                      </div>
                    </div>

                    {/* Batch Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Batch Size
                        <span className="text-xs text-gray-400 ml-2">Range: 10–500</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range" min="10" max="500" step="10"
                          value={configData.batch_size || 100}
                          onChange={(e) => setConfigData({...configData, batch_size: parseInt(e.target.value)})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                        <span className="text-sm font-mono font-semibold text-indigo-600 w-12 text-right">{configData.batch_size || 100}</span>
                      </div>
                    </div>

                    {/* Max Retries */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Retries
                        <span className="text-xs text-gray-400 ml-2">0 = unlimited, 1–10</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range" min="0" max="10" step="1"
                          value={configData.max_retries != null ? configData.max_retries : 0}
                          onChange={(e) => setConfigData({...configData, max_retries: parseInt(e.target.value)})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                        <span className="text-sm font-mono font-semibold text-indigo-600 w-16 text-right">
                          {(configData.max_retries != null ? configData.max_retries : 0) === 0 ? '∞' : configData.max_retries}
                        </span>
                      </div>
                    </div>

                    {/* Last Updated Info */}
                    {configData.updated_at && (
                      <div className="text-xs text-gray-400 pt-2 border-t">
                        Last updated: {new Date(configData.updated_at).toLocaleString()}
                        {configData.updated_by && ` by ${configData.updated_by}`}
                      </div>
                    )}
                  </>
                ) : (
                  /* Generic worker config — key/value text inputs */
                  Object.entries(configData).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => setConfigData({...configData, [key]: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      />
                    </div>
                  ))
                )
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No configuration data available</p>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t">
              <button onClick={() => setShowConfigModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={configSaving || Object.keys(configData).length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
              >
                <Save className="w-4 h-4" />
                {configSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedWorker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Worker Details — {selectedWorker.name}</h3>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider">Basic Information</h4>
                  <dl className="space-y-2.5">
                    {[
                      ['Worker Code', selectedWorker.code],
                      ['Name', selectedWorker.name],
                      ['Type', selectedWorker.type],
                      ['Host', selectedWorker.host],
                      ['Version', selectedWorker.version],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between">
                        <dt className="text-sm text-gray-500">{label}</dt>
                        <dd className="text-sm font-medium text-gray-900">{val || '—'}</dd>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd>{getStatusBadge(selectedWorker.status)}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider">Runtime</h4>
                  <dl className="space-y-2.5">
                    {[
                      ['PID', selectedWorker.pid || '—'],
                      ['Uptime', formatUptime(selectedWorker.uptime_seconds)],
                      ['Memory', selectedWorker.memory_usage && selectedWorker.memory_usage !== '0' ? selectedWorker.memory_usage : '—'],
                      ['Heartbeat', selectedWorker.heartbeat_status || '—'],
                      ['Batch Size', selectedWorker.batch_size],
                      ['Pickup Interval', `${selectedWorker.pickup_interval_ms}ms`],
                      ['Max Retry', selectedWorker.max_retry],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between">
                        <dt className="text-sm text-gray-500">{label}</dt>
                        <dd className="text-sm font-medium text-gray-900 font-mono">{val}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              {selectedWorker.description && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm uppercase tracking-wider">Description</h4>
                  <p className="text-sm text-gray-600">{selectedWorker.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Logs Drawer */}
      {showLogsDrawer && selectedWorker && (
        <WorkerLogsDrawer
          worker={selectedWorker}
          onClose={() => { setShowLogsDrawer(false); setSelectedWorker(null); }}
        />
      )}
    </div>
  );
};

// Logs Drawer — reads real log files from backend
const WorkerLogsDrawer = ({ worker, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const containerRef = useRef(null);

  useEffect(() => {
    fetchLogs();
  }, [worker.code]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${worker.code}/logs?limit=300`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = filterLevel === 'ALL' ? logs : logs.filter(l => l.level === filterLevel);

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400 bg-red-900/30';
      case 'WARNING': return 'text-yellow-400 bg-yellow-900/30';
      case 'INFO': return 'text-blue-400 bg-blue-900/30';
      case 'DEBUG': return 'text-gray-400 bg-gray-700/50';
      default: return 'text-gray-400 bg-gray-700/50';
    }
  };

  const formatTime = (ts) => {
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-3xl bg-gray-900 h-full flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-green-400" />
            <div>
              <span className="text-white font-medium text-sm">{worker.name}</span>
              <span className="text-gray-400 text-xs ml-2">({worker.code})</span>
            </div>
            {worker.status === 'running' && (
              <span className="px-2 py-0.5 text-[10px] bg-green-600 text-white rounded-full font-medium">LIVE</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-700 text-gray-300 border border-gray-600 rounded"
            >
              <option value="ALL">All Levels</option>
              <option value="ERROR">Errors</option>
              <option value="WARNING">Warnings</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>
            <button onClick={fetchLogs} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Log entries */}
        <div ref={containerRef} className="flex-1 overflow-y-auto p-4 font-mono text-xs leading-relaxed">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : filteredLogs.length > 0 ? (
            <div className="space-y-1">
              {filteredLogs.map((log, i) => (
                <div key={i} className="flex items-start gap-2 py-1 hover:bg-gray-800/50 rounded px-2 -mx-2">
                  <span className="text-gray-500 flex-shrink-0 w-24 text-right">{formatTime(log.timestamp)}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 w-16 text-center ${getLevelColor(log.level)}`}>
                    {log.level}
                  </span>
                  <span className="text-gray-300 break-all">{log.message}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <div className="text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No logs available</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between text-xs text-gray-400">
          <span>{filteredLogs.length} entries {filterLevel !== 'ALL' ? `(filtered: ${filterLevel})` : ''}</span>
          <span>{logs[0]?.source ? `Source: ${logs[0]?.source}` : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default WorkerManagement;
